/**
 * Applying a queued write to the local mirror, so offline looks like online (§8.2).
 *
 * The queue (`taskQueue.ts`) makes a write *survive*; this makes it *visible*. Without it a
 * queued completion left the row exactly as it was, so the tick appeared to do nothing, undo
 * had nothing to undo, and every card disagreed with what the reader had just done — the
 * "lots of things kinda broken" report that prompted this (DECISIONS.md D166).
 *
 * The rule is narrow: patch the cached responses the same way the server would have, then let
 * the cards re-read them exactly as they always do. Nothing here knows about React, and no
 * card needs new state — the mirror *is* the state, so a queued change and a server change
 * arrive by the identical route.
 *
 * These patches are guesses at what the server will do, and they are allowed to be: the next
 * successful read overwrites them wholesale with the truth. They only have to be right enough
 * to keep the screen honest until then, which is why each one does the least it can.
 */

import { setLocalAnswer, type Area, type CaptureTier, type InboxItem, type Note, type OpenTasks, type Project, type Task } from "./api";
import { cacheKeys, cachePatch, cacheRead } from "./readCache";

/** Every cached variant of a path — the open list is cached once per energy ceiling and
 * snoozed toggle, and a completed task has to leave all of them, not just the one on screen. */
function variantsOf(path: string): string[] {
  return cacheKeys().filter((key) => key === path || key.startsWith(`${path}?`));
}

function patchOpenTasks(update: (tasks: Task[]) => Task[]): void {
  for (const key of variantsOf("/api/briefing/open")) {
    cachePatch<OpenTasks>(key, (body) => {
      if (!Array.isArray(body?.tasks)) return body;
      const tasks = update(body.tasks);
      return { ...body, tasks, total: body.total + (tasks.length - body.tasks.length) };
    });
  }
}

function patchStarred(update: (tasks: Task[]) => Task[]): void {
  for (const key of variantsOf("/api/briefing/starred")) {
    // Guarded: a cached entry from an older shape, or a half-written one, must not turn a
    // patch into a crash — the mirror is convenience, and a bad entry is worth ignoring.
    cachePatch<{ tasks: Task[] }>(key, (body) =>
      Array.isArray(body?.tasks) ? { ...body, tasks: update(body.tasks) } : body,
    );
  }
}

/** Every mirrored list of tasks: the briefing's sections and any `/api/tasks?…` query. */
function patchTaskLists(update: (tasks: Task[]) => Task[]): void {
  patchOpenTasks(update);
  patchStarred(update);
  for (const key of variantsOf("/api/tasks")) {
    cachePatch<{ tasks: Task[]; count: number }>(key, (body) => {
      if (!Array.isArray(body?.tasks)) return body;
      const tasks = update(body.tasks);
      return { ...body, tasks, count: tasks.length };
    });
  }
}

/**
 * Applied everywhere a task can appear, so no card is left showing the old version —
 * **including inside a parent's embedded `subtasks`**, which is the only place a subtask ever
 * appears (§8.6.4: "reachable only through their parent").
 *
 * Missing that was why completing or undoing a subtask offline changed nothing visible: the
 * row it lives in was never touched, so the fold kept rendering the old state and the undo
 * had nothing to act on (D169).
 */
function updateTaskEverywhere(taskId: string, update: (task: Task) => Task): void {
  const applyOne = (task: Task): Task => {
    const next = task.id === taskId ? update(task) : task;
    if (!next.subtasks?.some((sub) => sub.id === taskId)) return next;

    const subtasks = next.subtasks.map((sub) => (sub.id === taskId ? update(sub) : sub));
    return {
      ...next,
      subtasks,
      subtask_done_count: subtasks.filter((sub) => sub.status === "done").length,
      subtask_total_count: subtasks.length,
    };
  };
  patchTaskLists((tasks) => tasks.map(applyOne));
  cachePatch<Task>(`/api/tasks/${taskId}`, update);
}

/**
 * A task completed offline.
 *
 * Marked `done` in place rather than removed, which is exactly what the online path does:
 * §8.5 holds a ticked row where it was, struck through, with the tick as its own undo, until
 * the next real load. Removing it here would break undo the same way a failed refetch used to.
 */
export function mirrorTaskCompleted(taskId: string, now = Date.now()): void {
  const ts = Math.floor(now / 1000);
  updateTaskEverywhere(taskId, (task) => ({
    ...task,
    status: "done",
    completed_at: ts,
    updated_at: ts,
    last_touched_at: ts,
  }));
}

/** A completion undone offline — the other half of the tick, and the reason completion marks
 * rather than removes. */
export function mirrorTaskReopened(taskId: string, now = Date.now()): void {
  const ts = Math.floor(now / 1000);
  updateTaskEverywhere(taskId, (task) => ({
    ...task,
    status: "open",
    completed_at: null,
    updated_at: ts,
  }));
}

/** Fields edited offline. Only what was sent is changed, matching `PATCH`'s own semantics. */
export function mirrorTaskPatched(taskId: string, fields: Partial<Task>, now = Date.now()): void {
  let updated: Task | undefined;
  updateTaskEverywhere(taskId, (task) => {
    updated = { ...task, ...fields, updated_at: Math.floor(now / 1000) };
    return updated;
  });

  // Starring moves a task *between lists*, which editing a field does not — the open list
  // excludes starred tasks (`OPEN_TASK_FILTER`) because Starred is its own section above it
  // (§8.2). Patching in place left the badge correct and both lists wrong: the task stayed in
  // Tasks and never appeared in Starred, and un-starring from Starred left it sitting there
  // (D170). Only acted on when `starred` was actually part of the change.
  if (updated === undefined || fields.starred === undefined) return;

  if (fields.starred) {
    patchOpenTasks((tasks) => tasks.filter((t) => t.id !== taskId));
    patchStarred((tasks) => [updated!, ...tasks.filter((t) => t.id !== taskId)]);
  } else {
    const returning = { ...updated, group: groupFor(updated) };
    patchStarred((tasks) => tasks.filter((t) => t.id !== taskId));
    patchOpenTasks((tasks) =>
      tasks.some((t) => t.id === taskId) ? tasks : [returning, ...tasks],
    );
  }
}

/** `DEFAULT_STALE_AFTER_DAYS` in `services/areas.py`. Only reached when neither the task's
 * project nor its area is mirrored, which is the same fallback the server's `COALESCE` uses. */
const DEFAULT_STALE_AFTER_DAYS = 14;

/** Whole days between a Unix timestamp and today, in the reader's own zone (§5.3). */
function daysSince(timestamp: number): number {
  const then = new Date(timestamp * 1000);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.max(0, Math.round((startOfDay(now) - startOfDay(then)) / 86_400_000));
}

/**
 * How long this task may go untouched before it counts as slipping — inherited from its
 * project, else its area, else the default (§8.5), which is exactly the `COALESCE` the
 * briefing query does in SQL.
 */
function staleAfterDaysFor(task: Task): number {
  if (task.stale_after_days !== undefined) return task.stale_after_days;

  if (task.project_id) {
    const projects = cacheRead<{ projects: Project[] }>("/api/projects");
    const project = projects?.projects.find((p) => p.id === task.project_id);
    if (project) return project.stale_after_days;
  }
  const areas = cacheRead<{ areas: Area[] }>("/api/areas");
  const area = areas?.areas.find((a) => a.id === task.area_id);
  return area?.stale_after_days ?? DEFAULT_STALE_AFTER_DAYS;
}

/**
 * Which section of the task list a row belongs to, in the same order `build_open_tasks`
 * decides it: overdue, then important, then slipping, then everything else (§8.2).
 *
 * Recomputed rather than carried, because a task coming back from Starred has whatever
 * `group` it had when it left, and the starred payload does not set one — so unstarring
 * dropped everything into "Everything else" (D171).
 *
 * **Including `slipping`.** An earlier version skipped it, on the grounds that staleness
 * thresholds are the server's to compute. That was wrong: the task carries its own
 * `last_touched_at`, and the thresholds live on the mirrored areas and projects, so every
 * input is already here (D172). Where the task still carries the briefing's own
 * `days_since_touch`/`stale_after_days`, those are used directly rather than recomputed.
 */
function groupFor(task: Task): Task["group"] {
  const today = new Date().toISOString().slice(0, 10);
  if (task.due !== null && task.due < today) return "overdue";
  if (task.important) return "important";

  const days = task.days_since_touch ?? daysSince(task.last_touched_at);
  // `>` not `>=`, matching the server: a task is slipping once it is *past* its threshold.
  return days > staleAfterDaysFor(task) ? "slipping" : "open";
}

/** Deferred offline: the task leaves the briefing until `until`, exactly as §8.2 describes,
 * so it disappears from the open list the way it would have online. */
export function mirrorTaskDeferred(taskId: string, until: string, now = Date.now()): void {
  updateTaskEverywhere(taskId, (task) => ({
    ...task,
    snoozed_until: until,
    deferred_count: task.deferred_count + 1,
    updated_at: Math.floor(now / 1000),
  }));
  patchOpenTasks((tasks) => tasks.filter((t) => t.id !== taskId));
  patchStarred((tasks) => tasks.filter((t) => t.id !== taskId));
}

/**
 * A task as the server will create it, built locally from what the Add form sent so the list
 * can show it now. The id is the one the queue will actually send (§5.1), so the real row
 * replaces this one rather than joining it — and any *further* offline action on it (ticking
 * it, editing it) patches the same row the server will eventually create.
 *
 * `area_name` is deliberately absent: the mirror does not know it, and inventing one would
 * put a wrong badge on the row. The card renders no area badge rather than a false one, until
 * the real record arrives.
 */
export function provisionalTask(
  id: string,
  fields: Omit<Partial<Task>, "id"> & { title: string },
): Task {
  const now = Math.floor(Date.now() / 1000);
  return {
    notes: null,
    status: "open",
    area_id: "",
    project_id: null,
    parent_id: null,
    sort_order: null,
    is_next_action: false,
    energy: null,
    important: false,
    starred: false,
    estimate_min: null,
    due: null,
    snoozed_until: null,
    rrule: null,
    recur_anchor: null,
    recur_start: null,
    completed_at: null,
    surfaced_count: 0,
    deferred_count: 0,
    starred_count: 0,
    ...fields,
    id,
    created_at: now,
    updated_at: now,
    last_touched_at: now,
    group: "open",
  };
}

/**
 * A task created offline, given the same client-minted id the queue will send (§5.1) — so a
 * later offline action on it (completing it, editing it) finds it in the mirror and patches
 * the same row the server will eventually create.
 *
 * **With no area it goes to the inbox instead**, because that is what the server will do with
 * it (§0: nothing is filed somewhere plausible-looking, so a task with nowhere to go is held
 * rather than guessed at). Putting it in the task list offline and watching it move to the
 * inbox on reconnect would be the app telling two different stories about the same action —
 * and reported as exactly that: "adding a new inbox item doesn't refresh the inbox" (D169).
 */
export function mirrorTaskCreated(task: Task): void {
  if (!task.area_id && !task.project_id) {
    // `provisionalTask` spreads the whole create payload in, so the Add card bulb's tier rode along.
    mirrorInboxItem(task.id, task.title, (task as Task & { tier?: CaptureTier }).tier);
    return;
  }

  // Exactly one list gets it, and every `/api/tasks?…` query does. `patchTaskLists` already
  // covers the open and starred lists, so calling `patchOpenTasks` alongside it added the
  // task to the open list twice, and its unconditional `patchStarred` put every new task in
  // Starred whether or not it was starred (D171).
  const prepend = (tasks: Task[]) => [task, ...tasks];
  for (const key of variantsOf("/api/tasks")) {
    cachePatch<{ tasks: Task[]; count: number }>(key, (body) =>
      Array.isArray(body?.tasks)
        ? { ...body, tasks: prepend(body.tasks), count: body.tasks.length + 1 }
        : body,
    );
  }
  if (task.starred) patchStarred(prepend);
  else patchOpenTasks(prepend);
}

/** An unrouted capture, held in the inbox the way `file_unrouted` would (§7). */
function mirrorInboxItem(id: string, rawText: string, tier?: CaptureTier): void {
  for (const key of variantsOf("/api/inbox")) {
    cachePatch<{ items: InboxItem[]; unresolved_count: number }>(key, (body) => ({
      ...body,
      items: [
        {
          id,
          raw_text: rawText,
          cleaned_text: null,
          source: "web",
          guess_kind: "task",
          guess_payload: null,
          entered_payload: null,
          // Not plumbed through from `bestEffortPosition()` — the offline mirror is
          // optimistic scaffolding for the moment before the real fetch confirms, and this
          // row is replaced by the server's own the instant connectivity returns. A location
          // badge that briefly said nothing and then appeared would be truthful, just not
          // worth the extra parameter yet.
          client_context: null,
          reason: "No area given - eFss will not guess one.",
          created_at: Math.floor(Date.now() / 1000),
          resolved_at: null,
          parse_attempts: 0,
          parsed_at: null,
          next_parse_at: null,
          // Which section it belongs in, as the server would say: this copy is what a slow or
          // dropped read falls back to, and without a state it landed under Unresolved
          // whatever the bulb said, then jumped to the Queue when a read finally got through.
          ...(tier ? { tier } : {}),
          state: tier && tier !== "off" ? "queued" : "unresolved",
        },
        ...body.items,
      ],
      unresolved_count: body.unresolved_count + 1,
    }));
  }
}

/**
 * A subtask split offline (§8.6.2): the original is **replaced** by its steps, as siblings at
 * its position — not given children, which the depth-1 rule forbids anyway.
 *
 * Mirrored separately from `mirrorSubtasksCreated` because the shapes genuinely differ: one
 * appends to a parent, the other substitutes within it. Sending a split through the "created"
 * patcher left the original in place and the replacements invisible, which is what "breaking
 * down a subtask did not display the splits" was (D169).
 */
export function mirrorSubtaskSplit(
  subtaskId: string,
  parentId: string,
  replacements: Task[],
): void {
  updateTaskEverywhere(parentId, (parent) => {
    const existing = parent.subtasks ?? [];
    const at = existing.findIndex((sub) => sub.id === subtaskId);
    if (at < 0) return parent;

    const subtasks = [...existing.slice(0, at), ...replacements, ...existing.slice(at + 1)];
    const wasNextAction = existing[at]?.is_next_action;
    return {
      ...parent,
      subtasks,
      subtask_total_count: subtasks.length,
      subtask_done_count: subtasks.filter((sub) => sub.status === "done").length,
      // §8.6.2: splitting the Next Action promotes the first replacement, silently.
      next_action_id: wasNextAction ? (replacements[0]?.id ?? null) : parent.next_action_id,
    };
  });
}

/** Subtasks added offline, attached to their parent the way `attach_subtasks` would (§8.6.4) —
 * reachable only through the parent, which is what the fold renders from. */
export function mirrorSubtasksCreated(parentId: string, subtasks: Task[]): void {
  updateTaskEverywhere(parentId, (parent) => {
    const existing = parent.subtasks ?? [];
    const merged = [...existing, ...subtasks];
    return {
      ...parent,
      subtasks: merged,
      subtask_total_count: merged.length,
      subtask_done_count: merged.filter((s) => s.status === "done").length,
      // The first step becomes the Next Action unless one is already open (§8.6), the same
      // rule `create_subtasks` applies server-side.
      next_action_id: parent.next_action_id ?? subtasks[0]?.id ?? null,
    };
  });
}

// -------------------------------------------------------------------------------------------
// Structure and the inbox (§4, §7.2)
// -------------------------------------------------------------------------------------------

/**
 * An area created offline, under the id the queue will send — so it is immediately selectable
 * as a destination, and a project or task filed into it offline refers to the same row the
 * server will eventually create.
 *
 * `open_tasks` starts at 0 and `status` at "active", which is what the server will produce
 * for a brand-new area; everything else the list needs is derived by the card.
 */
export function mirrorAreaCreated(id: string, name: string): void {
  for (const key of variantsOf("/api/areas")) {
    cachePatch<{ areas: Area[] }>(key, (body) => ({
      ...body,
      areas: [
        ...body.areas,
        { id, name, stale_after_days: 14, status: "active", sort_order: body.areas.length, open_tasks: 0 },
      ],
    }));
  }
}

export function mirrorProjectCreated(id: string, name: string, areaId: string): void {
  for (const key of variantsOf("/api/projects")) {
    cachePatch<{ projects: Project[] }>(key, (body) => ({
      ...body,
      projects: [
        ...body.projects,
        {
          id,
          area_id: areaId,
          name,
          outcome: null,
          status: "active",
          target_date: null,
          stale_after_days: 14,
          open_tasks: 0,
        },
      ],
    }));
  }
}

/**
 * An inbox item filed offline: it leaves the unresolved list, and whatever it became appears
 * wherever that kind of thing lives — so the decision visibly sticks instead of the item
 * sitting there still asking to be filed.
 */
export function mirrorInboxResolved(
  itemId: string,
  body: { action: string; id?: string; title?: string; area_id?: string | null },
): void {
  let raw = "";
  for (const key of variantsOf("/api/inbox")) {
    cachePatch<{ items: InboxItem[]; unresolved_count: number }>(key, (inbox) => {
      const item = inbox.items.find((i) => i.id === itemId);
      if (item) raw = item.raw_text;
      return {
        ...inbox,
        items: inbox.items.filter((i) => i.id !== itemId),
        unresolved_count: Math.max(0, inbox.unresolved_count - 1),
      };
    });
  }

  const name = (body.title || raw).trim();
  if (!body.id || !name) return;

  if (body.action === "area") mirrorAreaCreated(body.id, name);
  else if (body.action === "project" && body.area_id) {
    mirrorProjectCreated(body.id, name, body.area_id);
  } else if (body.action === "task") {
    mirrorTaskCreated(provisionalTask(body.id, { title: name, area_id: body.area_id ?? "" }));
  }
  else if (body.action === "note") {
    // Reported as filing to a note "silently failing": the item did leave the inbox, but the
    // note it became appeared nowhere, so the action looked like it had done nothing (D169).
    mirrorNoteCreated(body.id, { title: name, area_id: body.area_id ?? null });
  }
}

// -------------------------------------------------------------------------------------------
// Generic writes (§8.2) — the long tail opted in with `queueable: true` in `api.ts`
// -------------------------------------------------------------------------------------------

function removeTaskEverywhere(taskId: string): void {
  const drop = (tasks: Task[]) => tasks.filter((t) => t.id !== taskId);
  patchOpenTasks(drop);
  patchStarred(drop);
}

/**
 * Reflect a queued generic write locally, and return something shaped like the response the
 * caller was expecting.
 *
 * Dispatch is on the path because that is all the generic queue carries — the alternative was
 * a bespoke queue kind per endpoint, which would be a dozen branches differing only in how
 * they could be wrong (D168). Anything not recognised here is still *queued*; it simply has
 * no local effect until the real response arrives, which is the honest outcome for a write
 * whose consequences this file does not model.
 */
export function mirrorRequest(method: string, path: string, body?: unknown): unknown {
  const fields = (body ?? {}) as Record<string, unknown>;

  const task = /^\/api\/tasks\/([^/]+)$/.exec(path);
  if (task) {
    const id = task[1];
    if (method === "DELETE") {
      removeTaskEverywhere(id);
      return { id, deleted: true, events_kept: 0 };
    }
    // `status: "dropped"` leaves the open list the same way `done` does not: a dropped task
    // is gone from view, not held for undo (§7).
    if (fields.status === "dropped") removeTaskEverywhere(id);
    else if (fields.status === "open") mirrorTaskReopened(id);
    else mirrorTaskPatched(id, fields as Partial<Task>);
    return { id, ...fields };
  }

  const area = /^\/api\/areas\/([^/]+)$/.exec(path);
  if (area) {
    const id = area[1];
    for (const key of variantsOf("/api/areas")) {
      cachePatch<{ areas: Area[] }>(key, (b) => ({
        ...b,
        areas:
          method === "DELETE"
            ? b.areas.filter((a) => a.id !== id)
            : b.areas.map((a) => (a.id === id ? { ...a, ...fields } : a)),
      }));
    }
    return { id, ...fields, deleted: method === "DELETE" };
  }

  const project = /^\/api\/projects\/([^/]+)(\/complete)?$/.exec(path);
  if (project) {
    const id = project[1];
    const completing = Boolean(project[2]);
    for (const key of variantsOf("/api/projects")) {
      cachePatch<{ projects: Project[] }>(key, (b) => ({
        ...b,
        projects:
          method === "DELETE"
            ? b.projects.filter((p) => p.id !== id)
            : b.projects.map((p) =>
                p.id === id ? { ...p, ...(completing ? { status: "done" } : fields) } : p,
              ),
      }));
    }
    return { id, ...fields, deleted: method === "DELETE" };
  }

  const note = /^\/api\/notes\/([^/]+)$/.exec(path);
  if (note) {
    const id = note[1];
    for (const key of variantsOf("/api/notes")) {
      cachePatch<{ notes: Note[] }>(key, (b) => ({
        ...b,
        notes:
          method === "DELETE"
            ? b.notes.filter((n) => n.id !== id)
            : b.notes.map((n) => (n.id === id ? { ...n, ...fields } : n)),
      }));
    }
    return { id, ...fields, deleted: method === "DELETE" };
  }

  if (path === "/api/areas/order" && Array.isArray(fields.ids)) {
    const order = fields.ids as string[];
    for (const key of variantsOf("/api/areas")) {
      cachePatch<{ areas: Area[] }>(key, (b) => ({
        ...b,
        areas: [...b.areas].sort((x, y) => order.indexOf(x.id) - order.indexOf(y.id)),
      }));
    }
    return { areas: [] };
  }

  return {};
}

/** A note created offline, under the id the queue will send (§5.1). */
export function mirrorNoteCreated(
  id: string,
  note: { title: string; body?: string | null; area_id?: string | null; project_id?: string | null },
): void {
  const now = Math.floor(Date.now() / 1000);
  for (const key of variantsOf("/api/notes")) {
    cachePatch<{ notes: Note[] }>(key, (b) => ({
      ...b,
      notes: [
        {
          id,
          title: note.title,
          body: note.body ?? null,
          area_id: note.area_id ?? null,
          project_id: note.project_id ?? null,
          promoted_path: null,
          created_at: now,
          updated_at: now,
          ai_reasoning: null, // a human just created this offline
        },
        ...b.notes,
      ],
    }));
  }
}

// -------------------------------------------------------------------------------------------
// Answering a filtered read locally (§8.2, D169)
// -------------------------------------------------------------------------------------------

/**
 * The mirrored full task list — every open top-level task plus each parent's current Next
 * Action, which is the broadest thing `GET /api/tasks` can return. Primed while connected
 * (`primeMirror`), and the source every filtered task query is answered from offline.
 *
 * Deliberately *not* `/api/briefing/open`'s snapshot, which looks similar and is not: that one
 * excludes starred and recurring tasks, because the briefing shows those in their own
 * sections. Answering an area drilldown from it would silently under-report exactly the tasks
 * the reader cares most about — `TaskDrawer` says as much where it refuses to filter the
 * on-screen list for the same reason.
 *
 * `limit` is `MAX_LIMIT` from `routers/tasks.py`, and must stay at or below it: asking for
 * more is a 422, so the prime failed on every attempt and this key was never written — which
 * left every offline task query with nothing to answer from. Blackjack's search returning
 * nothing offline was that, and it was invisible because the prime swallows its own failures
 * by design (D171).
 */
export const TASK_MIRROR_PATH =
  "/api/tasks?status=open&limit=500&include_next_action_subtasks=true";

/**
 * Recompute `GET /api/tasks?…` from the mirrored full list, applying the same filters the
 * server would.
 *
 * This is what replaced the old "any cached variant will do" fallback: the parameters are
 * *understood* here rather than ignored, so unfolding an area shows that area's tasks, and a
 * search shows that search's results — offline exactly as online.
 */
function answerTasksQuery(path: string): { tasks: Task[]; count: number } | undefined {
  const mirrored = cacheRead<{ tasks: Task[] }>(TASK_MIRROR_PATH);
  if (mirrored === undefined) return undefined;

  const params = new URLSearchParams(path.split("?")[1] ?? "");
  const status = params.get("status") ?? "open";
  const area = params.get("area");
  const project = params.get("project");
  const energyMax = params.get("energy_max");
  const q = params.get("q")?.trim().toLowerCase();
  const limit = Number(params.get("limit") ?? 50);
  const withNextActions = params.get("include_next_action_subtasks") === "true";

  const tasks = mirrored.tasks.filter((task) => {
    // The mirror holds Next Action subtasks so Blackjack can ask for them; every other
    // caller expects top-level rows only (§8.6.4).
    if (task.parent_id !== null && !withNextActions) return false;
    if (status !== "all" && task.status !== status) return false;
    if (area !== null && task.area_id !== area) return false;
    if (project !== null && task.project_id !== project) return false;
    if (energyMax !== null && !withinEnergyCeiling(task, Number(energyMax))) return false;
    if (q && !task.title.toLowerCase().includes(q)) return false;
    return true;
  });

  return { tasks: tasks.slice(0, limit), count: Math.min(tasks.length, limit) };
}

/** §8.3's ceiling, as `open_tasks` applies it in SQL: under the ceiling, or unrated, or
 * flagged — the slider must never hide something that matters. */
function withinEnergyCeiling(task: Task, ceiling: number): boolean {
  return task.energy === null || task.energy <= ceiling || task.important || task.starred;
}

/**
 * A read this app can answer from a broader snapshot rather than from an identical earlier
 * request. Registered with `api.ts`, which calls it only after an exact mirror miss.
 *
 * Returning `undefined` means "I cannot answer this", and the caller then reports being
 * offline — which is the honest outcome, and much better than the wrong data the old
 * key-prefix fallback produced.
 */
/**
 * Recompute `GET /api/briefing/open?…` from the snapshot, which holds every open task with no
 * ceiling and no cap (D167). The energy filter is the only parameter that varies in practice,
 * and applying it here is what lets the slider work offline without the card knowing anything
 * about the mirror.
 */
function answerOpenTasks(path: string): OpenTasks | undefined {
  const snapshot = cacheRead<OpenTasks>("/api/briefing/open?snapshot=true");
  if (snapshot === undefined) return undefined;

  const params = new URLSearchParams(path.split("?")[1] ?? "");
  if (params.get("snapshot") === "true") return snapshot;

  const raw = params.get("energy");
  const ceiling = raw === null ? snapshot.energy : Number(raw);
  const tasks = snapshot.tasks.filter((t) => withinEnergyCeiling(t, ceiling));
  return { ...snapshot, tasks, total: tasks.length, truncated: false, energy: ceiling };
}

/**
 * Teach `api.ts` how to answer a read locally.
 *
 * Called explicitly at startup (`main.tsx`) rather than run as an import side effect: offline
 * reads working would otherwise depend on some unrelated module happening to import this one,
 * which is exactly the kind of invisible coupling that breaks quietly and much later.
 */
export function installOfflineMirror(): void {
  setLocalAnswer((path) => {
    if (path.startsWith("/api/tasks?") || path === "/api/tasks") return answerTasksQuery(path);
    if (path.startsWith("/api/briefing/open")) return answerOpenTasks(path);
    return undefined;
  });
}
